import type moment from 'moment'
import { useCallback, useContext, useId, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import CancellationPolicyModal from 'components/Common/CancellationPolicies/CancellationPolicyModal'
import CashRefundCancellationPolicyModal from 'components/Common/CancellationPolicies/Flash/CashRefundCancellationPolicyModal'
import OfferPropertyFlexibleCancellationPolicyTimelineModal from 'components/Common/CancellationPolicies/Flash/OfferPropertyFlexibleCancellationPolicyTimelineModal'
import { getCashRefundCancellationTimelineDetails } from 'components/Common/CancellationPolicies/Flash/cashRefundCancellationPolicyTimelineUtils'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationTimelineDetails,
} from 'components/Common/CancellationPolicies/Flash/flexibleCancellationPolicyTimelineUtils'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import Group from 'components/utils/Group'
import { CANCELLATION_POLICIES } from 'constants/cancellationPolicies'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { isCharterCancellationPolicy, isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import type { FlashCancellationOptionRow } from 'lib/offer/flashCancellationOptions'

import useFlashCancellationOptionCopy from './useFlashCancellationOptionCopy'

const StyledRadioInput = styled(RadioInput)`
  display: inline-flex;
  align-items: center;
`

const CancellationClickable = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  padding: 0;
  line-height: 0;
  color: inherit;
`

interface OptionRowProps {
  row: FlashCancellationOptionRow
  radioGroupName: string
  checkIn?: string | moment.Moment
  propertyTimezoneOffset?: number
  numberOfDateChangesAllowed?: string
  showReserveForZero: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

function FlashCancellationOptionRowInput(props: OptionRowProps) {
  const { row, radioGroupName, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed, showReserveForZero, onChange } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)
  const getCopy = useFlashCancellationOptionCopy()
  const copy = getCopy(row.policyType)
  const cancellationPolicy = useMemo<App.OfferCancellationPolicy | undefined>(
    () => row.policyType ? { type: row.policyType, policyDetail: [] } : undefined,
    [row.policyType],
  )
  const isFlexible = !!cancellationPolicy && isFlexibleCancellationPolicy(cancellationPolicy)
  const isCharter = isCharterCancellationPolicy(cancellationPolicy)
  const isCashRefund = row.policyType === CANCELLATION_POLICIES.CASH_REFUND_PRIOR_TO_CHECK_IN_TEN_DAYS
  const isRefundable = isFlexible || isCashRefund

  const flexibleDetails = useMemo(() => isFlexible && cancellationPolicy
    ? getFlexibleCancellationTimelineDetails({ cancellationPolicy, checkInDate: checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed })
    : undefined, [isFlexible, cancellationPolicy, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed])
  const cashRefundDetails = useMemo(() => isCashRefund
    ? getCashRefundCancellationTimelineDetails({ checkInDate: checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed })
    : undefined, [isCashRefund, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed])

  const deadline = checkIn ? (flexibleDetails?.startBasedOnCheckIn ?? cashRefundDetails?.deadline) : undefined
  const today = flexibleDetails?.today ?? cashRefundDetails?.today
  const formattedDeadline = deadline && today ? formatFlexibleCancellationPolicyDate(deadline, today, intlDateFormatter) : undefined

  const showCancellationDetails = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (flexibleDetails) {
      showModal(<OfferPropertyFlexibleCancellationPolicyTimelineModal details={flexibleDetails} isCharter={isCharter} />)
    } else if (cashRefundDetails) {
      showModal(<CashRefundCancellationPolicyModal details={cashRefundDetails} />)
    } else {
      showModal(<CancellationPolicyModal heading={copy.title} policyDescriptions={copy.description ? [copy.description] : []} />)
    }
  }, [showModal, flexibleDetails, isCharter, cashRefundDetails, copy])

  return (
    <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
      <StyledRadioInput
        name={radioGroupName}
        value={row.key}
        checked={row.isSelected}
        onChange={onChange}
      >
        <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
          <BodyText
            variant="medium"
            weight={isRefundable ? 'bold' : undefined}
            colour={isRefundable ? 'highlight-primary-dark' : undefined}
            wrap="no-wrap"
          >
            {copy.title}
          </BodyText>
          {formattedDeadline && (
            <BodyText variant="medium" wrap="no-wrap">
              <FormattedMessage
                id="hv1uXBUiZ3e8KSTtpNec"
                defaultMessage="until {date}"
                description="Date suffix for cancellation policy in the date selection or calendar picker"
                values={{ date: formattedDeadline }}
              />
            </BodyText>
          )}
          {row.priceDifference > 0 && (
            <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
              <FormattedMessage
                id="mGHDJoYlHarKV4E9f2Ln"
                defaultMessage="+{price}"
                description="Positive price difference with plus sign for refundability option"
                values={{ price: <FormatCurrency value={row.priceDifference} format="roundedDollar" /> }}
              />
            </BodyText>
          )}
          {row.priceDifference < 0 && (
            <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
              <FormatCurrency value={row.priceDifference} format="roundedDollar" />
            </BodyText>
          )}
          {showReserveForZero && <ReserveForZeroTag />}
        </Group>
      </StyledRadioInput>
      <CancellationClickable
        onClick={showCancellationDetails}
        aria-label={intl.formatMessage({
          id: 'gqj3SoRVAAsO3EFjMrzF',
          defaultMessage: 'Cancellation policy details for the {option} option',
          description: 'Accessible label for the info icon that opens the cancellation policy details of a flash cancellation option',
        }, { option: copy.title })}
      >
        <LineInfoCircleIcon size="XS" />
      </CancellationClickable>
    </Group>
  )
}

interface Props {
  rows: Array<FlashCancellationOptionRow>
  reserveForZeroKeys?: ReadonlySet<string>
  checkIn?: string | moment.Moment
  propertyTimezoneOffset?: number
  numberOfDateChangesAllowed?: string
  onChange: (key: string) => void
}

function FlashCancellationOptionSelector(props: Props) {
  const { rows, reserveForZeroKeys, checkIn, propertyTimezoneOffset, numberOfDateChangesAllowed, onChange } = props
  const intl = useIntl()
  const radioGroupName = useId()

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }, [onChange])

  if (rows.length < 2) {
    return null
  }

  return (
    <RadioGroup
      aria-label={intl.formatMessage({
        id: 'J3zjEK2A6_vz3klS2v6N',
        defaultMessage: 'Cancellation options',
        description: 'Accessible label for the radio group that lets the customer choose a cancellation option on a flash hotel package',
      })}
    >
      {rows.map((row) => (
        <FlashCancellationOptionRowInput
          key={row.key}
          row={row}
          radioGroupName={radioGroupName}
          checkIn={checkIn}
          propertyTimezoneOffset={propertyTimezoneOffset}
          numberOfDateChangesAllowed={numberOfDateChangesAllowed}
          showReserveForZero={!!reserveForZeroKeys?.has(row.key)}
          onChange={handleChange}
        />
      ))}
    </RadioGroup>
  )
}

export default FlashCancellationOptionSelector
